import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Spinner, Image, Button } from 'react-bootstrap';
import { GeoAlt, PencilFill, Telephone, Envelope, BoxArrowUpRight } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { usePageSearch } from '../navigation/SearchContext';
import { fetchOfficialsDirectory } from '../../utils/officialsDirectory';
import {
  SEAT_GROUP,
  buildSeatSections,
  fetchOfficialsForDistricts,
  groupForLevelChamber,
  mergeOfficialSources,
} from '../../utils/districtsApi';
import { useHomeLocation, describeLocation, hasDistricts } from '../../utils/homeLocation';
import { loadAllMembers, searchMembers } from '../../utils/nationalMembers';
import LocationPicker from './LocationPicker';
import './Representatives.css';
import './RepresentativesModes.css';

// This page is location-first. There is no nationwide mode -- it answers "who
// represents this place" -- so with nothing saved there is nothing to render and
// the picker is the whole screen. The previous all-535-members browse came from
// api.govglance.org/members/; if that view is wanted back it belongs behind its
// own tab against the old endpoint, not here.
//
// Two sources feed it, and they own different rows:
//
//   /districts/officials  the district-drawn seats -- U.S. House, state upper,
//                         state lower. Queried with the saved district numbers,
//                         so the street address is not sent on every load.
//   /officials/directory  everything with no district: U.S. Senators, the
//                         governor, and city and county officeholders.
//
// The two are combined by mergeOfficialSources rather than one replacing the
// other. That matters for the House: the districts payload names the right
// member, but the bioguide id that /us/representative/:bioGuideId is built from
// lives on the directory row. Dropping the directory duplicate outright is what
// takes the link away, so the district row is filled in from it first.
//
// If the districts call fails, or no districts are saved, the directory carries
// the whole page -- which is the behaviour from before this migration.
//
// Ordering is always by seat. The API returns officials grouped by seat, and a
// seat is the unit a person actually thinks in, so there is no sort control --
// name and party orderings cut across seats by definition and flattened the one
// grouping that carries meaning. District order is not re-derived here either:
// district labels are not numbers (Idaho has 27 and 27A, New Hampshire has
// Belknap 1 and Carroll 1), so the natural order api2 returns is carried on each
// row as `order` and used by the grouped view.
//
// The page answers one question: who holds office here. Candidates are not
// reachable from this page.
//
// County and city are separate sections rather than one "City and county"
// heading. They are separate governments, the directory already returns them in
// separate buckets, and the single heading was the only place in the app where
// two governments shared one. The Local tab still covers both together, which
// is what api2 means by level=local.
//
// ─── Searching everyone ───────────────────────────────────────────────────────
//
// The search filters the reader's own officials instantly, and from two
// characters on it also searches every current member of Congress
// (utils/nationalMembers). Those matches come in a section of their own under
// the reader's officials, with anyone already listed above left out, so
// nobody appears twice. The full member list is read once, the first time
// somebody searches, and kept for the session. It covers Congress only: there
// is no nationwide source of state and local officeholders.

// Most nationwide matches shown at once. A one-letter-away query like "an"
// matches hundreds of names; past this the reader narrows the search instead of
// scrolling.
const NATIONWIDE_LIMIT = 30;

// --- Display -----------------------------------------------------------------
const isFederal = (official) => official.level === 'federal';
const isSenator = (official) => isFederal(official) && official.chamber === 'senate';

// --- Page --------------------------------------------------------------------
const RepresentativesPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const { location, saveLocation, mergeLocation } = useHomeLocation();

  const [officials, setOfficials] = useState([]);
  const [unfilledSeats, setUnfilledSeats] = useState([]);
  const [seatNote, setSeatNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [view, setView] = useState('all'); // all | federal | state | local
  const [showPicker, setShowPicker] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);

  // Every current member of Congress, for the nationwide results. null until
  // the first search asks for it.
  const [allMembers, setAllMembers] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(null);

  // Take over the shared search bar while mounted. Both IntegratedSearchNavBar
  // (desktop top bar) and BottomNavbar (mobile) hand off to onSearch instead of
  // routing to /search.
  //
  // onSearch must be referentially stable: usePageSearch lists it in its effect
  // deps, so a fresh function each render would re-register in a loop. setQuery
  // is a useState setter, so it is stable -- pass it directly rather than
  // wrapping it in an arrow.
  usePageSearch({
    placeholder: t('common:searchRepresentatives', 'Search representatives'),
    onSearch: setQuery,
    value: query,
  });

  const searching = query.trim().length >= 2;

  // The member list is read the first time a search needs it, and only then:
  // somebody who opens the page to see their own officials never downloads it.
  //
  // membersLoading is deliberately not a dependency: setting it would re-run
  // this effect, and the cleanup would then discard the answer it is waiting
  // for. loadAllMembers shares one request per session, so a re-run while the
  // first is still in flight simply waits on the same promise.
  useEffect(() => {
    if (!searching || allMembers) return undefined;

    let live = true;
    setMembersLoading(true);
    setMembersError(null);

    loadAllMembers()
      .then((members) => {
        if (live) setAllMembers(members);
      })
      .catch((err) => {
        console.warn('[RepresentativesPage] nationwide members failed', err?.message);
        if (live) setMembersError(err);
      })
      .finally(() => {
        if (live) setMembersLoading(false);
      });

    return () => {
      live = false;
    };
  }, [searching, allMembers]);

  // Refetch whenever the saved location changes. The deps are the fields the
  // requests are actually built from, not the object -- useHomeLocation returns
  // a fresh object on every storage event, which would loop.
  const {
    address,
    city,
    stateAbbr,
    congressionalDistrict,
    stateUpperDistrict,
    stateLowerDistrict,
  } = location || {};

  useEffect(() => {
    if (!stateAbbr) {
      setOfficials([]);
      setUnfilledSeats([]);
      return undefined;
    }

    const controller = new AbortController();
    const wantDistricts = Boolean(
      congressionalDistrict || stateUpperDistrict || stateLowerDistrict
    );

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setSeatNote(null);

        // allSettled rather than all: either source failing on its own is a
        // partial page, not a dead one.
        const [districtOutcome, directoryOutcome] = await Promise.allSettled([
          wantDistricts
            ? fetchOfficialsForDistricts(
                {
                  stateAbbr,
                  congressional: congressionalDistrict,
                  stateUpper: stateUpperDistrict,
                  stateLower: stateLowerDistrict,
                },
                { signal: controller.signal }
              )
            : Promise.resolve(null),
          fetchOfficialsDirectory({ address, city, stateAbbr }, { signal: controller.signal }),
        ]);

        if (controller.signal.aborted) return;

        if (districtOutcome.status === 'rejected' && directoryOutcome.status === 'rejected') {
          // Both sources are down, which is the only case that leaves nothing
          // to render. Logged rather than swallowed: the two failed for their
          // own reasons and the page shows one sentence for both, so without
          // this there is no way to tell which -- or whether one of them is a
          // request that failed and the other a response that could not be
          // read.
          console.error('[RepresentativesPage] both official sources failed', {
            request: {
              stateAbbr,
              address,
              city,
              congressionalDistrict,
              stateUpperDistrict,
              stateLowerDistrict,
            },
            districts: {
              message: districtOutcome.reason?.message,
              status: districtOutcome.reason?.status,
            },
            directory: {
              message: directoryOutcome.reason?.message,
              status: directoryOutcome.reason?.status,
            },
          });

          setError(districtOutcome.reason || directoryOutcome.reason);
          setOfficials([]);
          setUnfilledSeats([]);
          return;
        }

        // One source failing on its own is a partial page rather than a dead
        // one, so it is logged and not shown. A section quietly missing is
        // still worth knowing about.
        if (districtOutcome.status === 'rejected') {
          console.warn(
            '[RepresentativesPage] districts source failed',
            districtOutcome.reason?.message
          );
        }
        if (directoryOutcome.status === 'rejected') {
          console.warn(
            '[RepresentativesPage] directory source failed',
            directoryOutcome.reason?.message
          );
        }

        const districtData =
          districtOutcome.status === 'fulfilled' ? districtOutcome.value : null;

        const districtOfficials = (districtData?.seats || []).flatMap((seat) => seat.officials);

        // Directory rows arrive without a group, so place them by their own
        // level/chamber pair before merging.
        const directoryOfficials = (
          directoryOutcome.status === 'fulfilled' ? directoryOutcome.value?.officials || [] : []
        ).map((official, index) => ({
          ...official,
          group: groupForLevelChamber(official.level, official.chamber),
          order: index,
        }));

        setOfficials(mergeOfficialSources(districtOfficials, directoryOfficials));
        setUnfilledSeats(districtData?.unfilledSeats || []);
        setSeatNote(districtData?.note || null);

        // The directory echoes the county it resolved on every response, and
        // the county is the one part of an address the user never types. This
        // page no longer uses it, but it is still kept on the saved location
        // for anything elsewhere in the app that filters by county, rather
        // than discarded and asked for again later.
        //
        // A merge, not a save: it fills a blank and never overwrites anything
        // the user chose. The effect's deps are the request fields, which this
        // does not touch, so it cannot loop.
        const resolved =
          directoryOutcome.status === 'fulfilled' ? directoryOutcome.value?.location : null;
        if (resolved?.county) {
          mergeLocation({
            county: resolved.county,
            countyGeoid: resolved.countyGeoid,
          });
        }
      } catch (err) {
        // Everything after the two responses land throws into here: merging,
        // grouping, normalization, the location merge. A TypeError in any of
        // them renders the same sentence as a dead API, so it says which it
        // was rather than leaving the two indistinguishable.
        if (err.name !== 'AbortError') {
          console.error('[RepresentativesPage] failed after fetch', err);
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [
    address,
    city,
    stateAbbr,
    congressionalDistrict,
    stateUpperDistrict,
    stateLowerDistrict,
    mergeLocation,
  ]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return officials.filter((official) => {
      if (view === 'federal' && official.level !== 'federal') return false;
      if (view === 'state' && official.level !== 'state') return false;
      if (view === 'local' && ['federal', 'state'].includes(official.level)) return false;
      if (!needle) return true;

      const haystack = [
        official.name,
        official.office,
        official.party,
        official.jurisdictionName,
        official.districtLabel,
        official.district ? `district ${official.district}` : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(needle);
    });
  }, [officials, query, view]);

  const sections = useMemo(() => {
    // Vacant seats are only shown on an unfiltered view -- they have no
    // officials, so they can never match a search and would otherwise appear as
    // stray empty sections in a filtered list.
    const seats = query.trim() ? [] : unfilledSeats;
    return buildSeatSections(visible, seats);
  }, [visible, unfilledSeats, query]);

  // Members of Congress matching the search, nationwide, minus anyone already
  // among the reader's own officials above. Federal only, so the State and
  // Local tabs leave it out.
  const nationwide = useMemo(() => {
    if (!searching || !allMembers || view === 'state' || view === 'local') {
      return { rows: [], total: 0 };
    }

    const listed = new Set(officials.map((official) => official.bioGuideId).filter(Boolean));
    const matches = searchMembers(allMembers, query).filter(
      (member) => !listed.has(member.bioGuideId)
    );

    const rows = matches.slice(0, NATIONWIDE_LIMIT).map((member) => {
      const senate = member.chamber === 'senate';
      return {
        ...member,
        office: senate
          ? t('common:usSenator', 'U.S. Senator')
          : t('common:usRepresentative', 'U.S. Representative'),
        jurisdictionName: senate
          ? member.stateName
          : [
              member.stateName,
              member.district ? `${t('common:district', 'District')} ${member.district}` : null,
            ]
              .filter(Boolean)
              .join(' \u00b7 '),
      };
    });

    return { rows, total: matches.length };
  }, [searching, allMembers, view, officials, query, t]);

  const counts = useMemo(
    () => ({
      federal: officials.filter((o) => o.level === 'federal').length,
      state: officials.filter((o) => o.level === 'state').length,
      local: officials.filter((o) => !['federal', 'state'].includes(o.level)).length,
    }),
    [officials]
  );

  // Federal officials have a bioguide ID and a detail page. State legislators and
  // local officeholders have neither, so they expand in place -- the directory
  // already carries their contact details, job description and action links.
  //
  // A federal row that reached here without a bioguide id -- the districts
  // payload had none and no directory row matched by name -- expands as well.
  // That is the honest outcome: /us/representative/undefined is a broken page,
  // and the contact details in the expansion are still useful.
  const openOfficial = useCallback(
    (official) => {
      if (isFederal(official) && official.bioGuideId) {
        navigate(
          isSenator(official)
            ? `/us/senator/${official.bioGuideId}`
            : `/us/representative/${official.bioGuideId}`,
          {
            state: {
              name: official.name,
              party: official.partyAbbr || official.party,
              // Flat string: MemberDetailPage normalizes both this and the old
              // address_information object shape.
              address: official.officeAddress,
              phone: official.phone,
              // MemberDetailPage branches on `district !== ""`; undefined would
              // render "undefinedth District".
              district: official.district || '',
              webURL: official.website,
              // Must stay the API's raw wording: MemberDetailPage does an exact
              // `chamber !== 'Senate'` check to decide whether to show Votes.
              chamber: isSenator(official) ? 'Senate' : 'House of Representatives',
              imageSource: official.photoUrl,
              // Null for every senator in api2 -- the subtitle omits it rather
              // than rendering a dangling separator.
              startYear: official.termStartYear,
              // A nationwide match names its own state; the reader's own
              // officials are in the saved one.
              stateName: official.stateName || location?.stateName,
            },
          }
        );
        return;
      }
      setExpandedKey((current) => (current === official.key ? null : official.key));
    },
    [navigate, location]
  );

  const tabs = [
    { key: 'all', label: t('common:all', 'All') },
    { key: 'federal', label: t('common:federal', 'Federal'), count: counts.federal },
    { key: 'state', label: t('common:state', 'State'), count: counts.state },
    // Still one tab covering county and city together, which is what api2 means
    // by level=local. The sections beneath it are what got split.
    { key: 'local', label: t('common:local', 'Local'), count: counts.local },
  ];

  // Section headings use the place's own name wherever one came back, and only
  // fall back to the generic word when it did not. "Hamilton County" is a
  // heading; "County" is a label pretending to be one.
  const sectionTitle = (section) => {
    if (section.group === SEAT_GROUP.STATEWIDE) {
      return t('common:statewideSeats', 'Statewide');
    }
    if (section.group === SEAT_GROUP.COUNTY) {
      return section.jurisdictionName || t('common:countySeats', 'County');
    }
    if (section.group === SEAT_GROUP.CITY) {
      return section.jurisdictionName || t('common:citySeats', 'City');
    }
    if (section.group === SEAT_GROUP.LOCAL) {
      // other_local: special districts, tribal governments. Not a new key for
      // the sake of one, but common:localSeats is already translated as "City
      // and county", which is the one thing this section is not.
      return section.jurisdictionName || t('common:otherLocalSeats', 'Other local government');
    }
    if (section.districtLabel) return section.districtLabel;

    const chamber =
      section.chamberName ||
      (section.group === SEAT_GROUP.CONGRESSIONAL
        ? t('common:usHouse', 'U.S. House')
        : t('common:stateLegislature', 'State legislature'));

    return section.district
      ? `${chamber} · ${t('common:district', 'District')} ${section.district}`
      : chamber;
  };

  const renderOfficial = (official) => {
    const expanded = expandedKey === official.key;
    const hasDetailPage = isFederal(official) && official.bioGuideId;

    return (
      <Container
        id="feedContainerNormalView"
        key={official.key}
        className="reps-card"
        onClick={() => openOfficial(official)}
        role="button"
        tabIndex={0}
        aria-expanded={hasDetailPage ? undefined : expanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openOfficial(official);
          }
        }}
      >
        <div className="reps-card-inner">
          {official.photoUrl ? (
            <div id="profileImage">
              <Image
                id="profileImage-image"
                src={official.photoUrl}
                alt=""
                // Photos are hotlinked from party and campaign CDNs rather than a
                // single official host, so individual images fail routinely.
                // Hide the frame instead of leaving a broken image icon.
                onError={(e) => {
                  e.currentTarget.closest('#profileImage').style.display = 'none';
                }}
              />
            </div>
          ) : (
            <div className="reps-avatar-fallback" aria-hidden="true">
              {official.name.charAt(0)}
            </div>
          )}

          <div className="reps-card-text">
            <div className="reps-name">{official.name}</div>
            {/* The seat is always the section heading now, so the district is
                never repeated on the card under it. */}
            <div className="reps-meta">
              <span>{official.office}</span>
            </div>
            <div className="reps-meta-secondary">
              {official.party}
              {/* The jurisdiction is the county or city section heading now, so
                  repeating it on every card under that heading says the same
                  thing twice. Kept for federal and state rows, where the
                  heading is a district rather than a place. */}
              {official.jurisdictionName &&
              !['county', 'city'].includes(official.level)
                ? `${official.party ? ' · ' : ''}${official.jurisdictionName}`
                : ''}
            </div>
          </div>
        </div>

        {expanded && (
          <div className="reps-card-detail" onClick={(e) => e.stopPropagation()}>
            {official.jobDescription && (
              // Describes what the office does, not who holds it -- safe to show
              // verbatim for every occupant of the seat.
              <p className="reps-job-description">{official.jobDescription}</p>
            )}

            <div className="reps-contact">
              {official.phone && (
                <a className="reps-contact-link" href={`tel:${official.phone}`}>
                  <Telephone className="me-2" />
                  {official.phone}
                </a>
              )}
              {official.email && official.email.includes('@') && (
                <a className="reps-contact-link" href={`mailto:${official.email}`}>
                  <Envelope className="me-2" />
                  {official.email}
                </a>
              )}
            </div>

            {/* Rendered from `actions` rather than the bare `url` field: some
                rows point `url` at an aggregator instead of the officeholder's
                own site. Actions the API labelled itself keep that label;
                the ones this app derives carry a key to translate. */}
            <div className="reps-actions">
              {(official.actions || []).map((action) => (
                <a
                  key={action.url}
                  className="reps-action"
                  href={action.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {action.labelKey ? t(action.labelKey, action.label) : action.label}
                  <BoxArrowUpRight className="ms-2" size={12} />
                </a>
              ))}
            </div>
          </div>
        )}
      </Container>
    );
  };

  const renderSection = (section) => (
    <section className="reps-group" key={section.key}>
      <h2 className="reps-group-title">{sectionTitle(section)}</h2>

      {section.vacant ? (
        // An unfilled seat is reported rather than 404'd, so it is said out
        // loud. A section that simply vanished would read as a bug in the app.
        <div className="reps-vacant">
          {t('common:seatVacant', 'No one currently holds this seat.')}
        </div>
      ) : (
        section.officials.map(renderOfficial)
      )}
    </section>
  );

  // Members of Congress from anywhere, under the reader's own officials. Shown
  // only while searching, and never on the State or Local tabs.
  //
  // When the search already found someone among the reader's own officials --
  // looking up one of their own senators, say -- the section stays out of the
  // way: no spinner while the member list loads, no "nobody else matches"
  // once it has, and no error if it fails. It appears only if it has somebody
  // to add. The spinner and those messages are for a search that found
  // nothing locally, where the nationwide list is the only answer coming.
  const renderNationwide = () => {
    if (!searching || view === 'state' || view === 'local') return null;

    const foundLocally = visible.length > 0;
    const pending = membersLoading || (!allMembers && !membersError);

    if (foundLocally && (pending || membersError || !nationwide.rows.length)) return null;

    let body;
    if (pending) {
      body = (
        <div className="reps-status reps-status--inline">
          <Spinner animation="grow" size="sm" className="themed-spinner" />
        </div>
      );
    } else if (membersError) {
      body = (
        <div className="reps-vacant">
          {t('common:nationwideUnavailable', 'Members of Congress nationwide could not be loaded right now.')}
        </div>
      );
    } else if (!nationwide.rows.length) {
      body = (
        <div className="reps-vacant">
          {t('common:nationwideNone', 'No member of Congress elsewhere matches that search.')}
        </div>
      );
    } else {
      body = (
        <>
          {nationwide.rows.map(renderOfficial)}
          {nationwide.total > nationwide.rows.length && (
            <div className="reps-vacant">
              {t('common:nationwideMore', '{{count}} more match. Keep typing to narrow the search.', {
                count: nationwide.total - nationwide.rows.length,
              })}
            </div>
          )}
        </>
      );
    }

    return (
      <section className="reps-group" key="nationwide">
        <h2 className="reps-group-title">
          {t('common:nationwideMembers', 'Members of Congress nationwide')}
        </h2>
        {body}
      </section>
    );
  };

  const renderBody = () => {
    if (!location?.stateAbbr) {
      // No saved place means no officials of the reader's own, but a search for
      // a member of Congress still works.
      return (
        <>
          {!searching && (
            <div className="reps-status">
              <p>{t('common:setLocationPrompt', 'Set your location to see who represents you.')}</p>
              <Button id="standardButton" onClick={() => setShowPicker(true)}>
                <GeoAlt className="me-2" />
                {t('common:setLocation', 'Set location')}
              </Button>
            </div>
          )}
          {searching && <div className="reps-list">{renderNationwide()}</div>}
        </>
      );
    }

    if (loading) {
      return (
        <div className="reps-status">
          <Spinner animation="grow" className="themed-spinner" />
        </div>
      );
    }

    if (error && !searching) {
      return (
        <div className="reps-status">
          <p>{t('common:membersLoadError', 'Could not load officials right now.')}</p>
        </div>
      );
    }

    const nationwideSection = renderNationwide();

    if (!visible.length && !sections.length && !nationwideSection) {
      // A location with no local rows is normal rather than broken: city and
      // county coverage depends on whether a roster page was found for that
      // government.
      const localGap = view === 'local' && !counts.local;
      return (
        <div className="reps-status">
          <p>
            {localGap
              ? t(
                  'common:noLocalOfficials',
                  'No city or county officials are archived for this place yet.'
                )
              : t('common:noMembersFound', 'No officials match that search.')}
          </p>
        </div>
      );
    }

    return (
      <div className="reps-list">
        {sections.map(renderSection)}
        {nationwideSection}
      </div>
    );
  };

  return (
    <div id="homebg" className="reps-page">
      {location?.stateAbbr && (
        <>
          {/* The location button shares a row with the tabs but sits outside the
              tablist: role="tablist" must contain only role="tab" children, or
              screen readers announce the location control as a fourth tab. */}
          <div className="reps-tabs-row">
            <button
              type="button"
              className="reps-location-button"
              onClick={() => setShowPicker(true)}
            >
              {location.stateFlag && (
                <Image src={location.stateFlag} alt="" className="reps-subtitle-flag" />
              )}
              <span>{describeLocation(location)}</span>
              <PencilFill size={11} className="reps-location-pencil" />
            </button>

            <div className="reps-tabs" role="tablist">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={view === tab.key}
                  className={`reps-tab ${view === tab.key ? 'reps-tab-active' : ''}`}
                  onClick={() => setView(tab.key)}
                >
                  {tab.label}
                  {tab.count ? ` (${tab.count})` : ''}
                </button>
              ))}
            </div>
          </div>

          {/* The controls row is now only the active-query pill, so it is not
              rendered at all when there is nothing to clear -- an empty row
              would leave a gap under the tabs. */}
          {query && (
            <div className="reps-controls">
              <button
                type="button"
                className="reps-active-query"
                onClick={() => setQuery('')}
                title={t('common:clearSearch', 'Clear search')}
              >
                {query} ×
              </button>
            </div>
          )}

          {/* Districts are drawn by block, so without them the list is the whole
              delegation rather than the user's own seats. The test is whether
              districts are known, not whether an address was given -- they can
              now be picked by hand, and someone who did that is not missing
              anything.

              A pill rather than the sentence-long link it used to be: it opens
              the same picker the location chip does, so it should read as the
              same kind of control. New key, not addAddressForDistrict -- that
              one is already translated as the long sentence, and i18next only
              falls back when a key is missing. */}
          {!hasDistricts(location) && (
            <div className="reps-precision-row">
              <button
                type="button"
                className="reps-precision-nudge"
                onClick={() => setShowPicker(true)}
              >
                <GeoAlt size={13} />
                {t('common:selectDistrict', 'Select your district')}
              </button>
            </div>
          )}

          {/* Populated when a district number matched in more than one chamber.
              api2 answers with both and says why rather than guessing. */}
          {seatNote && <div className="reps-seat-note">{seatNote}</div>}
        </>
      )}

      {renderBody()}

      <div className="reps-bottom-spacer" />

      <LocationPicker
        show={showPicker}
        onHide={() => setShowPicker(false)}
        currentLocation={location}
        onSave={saveLocation}
      />

      <style>{`
        /* The spinner under the nationwide heading while the member list
           loads: a line, not the page-height block the page spinner gets. */
        .reps-status--inline { padding: 12px 0; }
      `}</style>
    </div>
  );
};

export default RepresentativesPage;